import React, { FC, useState } from 'react';
import type {
  OrganisationMember,
  CreateOrganisationGroupRequest,
  OrganisationCreditPolicyOverride,
  OrganisationGroup,
  OrganisationSeatPlan,
  UpdateOrganisationGroupRequest,
} from '@dreamai/types';
import { SEAT_LABELS, STATUS_LABELS } from './organisationLabels';
import { formatCredits } from './organisationCreditLabels';
import { parseOptionalNumber } from './OrganisationCreditPolicyForm';
import { planSentence } from './organisationPlans';
import OrganisationPlanPicker from './OrganisationPlanPicker';

/** Group-level credit override; null clears the field. */
export interface GroupOverrideInput {
  allocation: number | null;
  overflowCap: number | null;
}

interface OrganisationGroupsProps {
  groups: OrganisationGroup[];
  overrides: OrganisationCreditPolicyOverride[];
  members: OrganisationMember[];
  defaultPlanMultiplier: number;
  seatPlan: OrganisationSeatPlan | null;
  busyKey: string | null;
  onCreate: (
    input: CreateOrganisationGroupRequest,
    override: GroupOverrideInput,
  ) => Promise<boolean>;
  onUpdate: (
    group: OrganisationGroup,
    patch: UpdateOrganisationGroupRequest,
    override: GroupOverrideInput,
  ) => Promise<boolean>;
  onDelete: (group: OrganisationGroup) => Promise<boolean>;
  onToggleMember: (
    group: OrganisationGroup,
    member: OrganisationMember,
    assigned: boolean,
  ) => Promise<boolean>;
}

interface GroupFormValues {
  name: string;
  description: string;
  /** null follows the organisation plan */
  planMultiplier: number | null;
  allocation: string;
  overflowCap: string;
}

const EMPTY_FORM: GroupFormValues = {
  name: '',
  description: '',
  planMultiplier: null,
  allocation: '',
  overflowCap: '',
};

function formFor(
  group: OrganisationGroup,
  override: OrganisationCreditPolicyOverride | undefined,
): GroupFormValues {
  return {
    name: group.name,
    description: group.description ?? '',
    planMultiplier: group.planMultiplier,
    allocation: override?.allocation == null ? '' : String(override.allocation),
    overflowCap:
      override?.overflowCap == null ? '' : String(override.overflowCap),
  };
}

function validate(values: GroupFormValues):
  | { error: string }
  | {
      input: CreateOrganisationGroupRequest;
      override: GroupOverrideInput;
    } {
  const name = values.name.trim();
  if (!name) return { error: 'Give the group a name.' };
  const allocation = parseOptionalNumber(values.allocation);
  const overflowCap = parseOptionalNumber(values.overflowCap);
  if (allocation === undefined || overflowCap === undefined) {
    return { error: 'Credit amounts must be whole numbers, or left blank.' };
  }
  return {
    input: {
      name,
      description: values.description.trim() || null,
      planMultiplier: values.planMultiplier,
    },
    override: { allocation, overflowCap },
  };
}

interface GroupFormProps {
  values: GroupFormValues;
  busy: boolean;
  submitLabel: string;
  seatPlan: OrganisationSeatPlan | null;
  defaultPlanMultiplier: number;
  onChange: (values: GroupFormValues) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

const GroupForm: FC<GroupFormProps> = ({
  values,
  busy,
  submitLabel,
  seatPlan,
  defaultPlanMultiplier,
  onChange,
  onSubmit,
  onCancel,
}) => {
  const set = (patch: Partial<GroupFormValues>) =>
    onChange({ ...values, ...patch });

  return (
    <form
      className='organisation-group-form'
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className='organisation-field-grid'>
        <label className='organisation-field'>
          <span className='field-label'>Name</span>
          <input
            type='text'
            className='field-input'
            placeholder='Partners'
            value={values.name}
            maxLength={80}
            disabled={busy}
            onChange={(e) => set({ name: e.target.value })}
          />
        </label>
        <label className='organisation-field'>
          <span className='field-label'>Description (optional)</span>
          <input
            type='text'
            className='field-input'
            value={values.description}
            maxLength={500}
            disabled={busy}
            onChange={(e) => set({ description: e.target.value })}
          />
        </label>
      </div>

      <div className='organisation-field'>
        <span className='field-label'>Plan for this group</span>
        {seatPlan ? (
          <OrganisationPlanPicker
            value={values.planMultiplier}
            seatPlan={seatPlan}
            disabled={busy}
            inherit={{
              label: 'Same as everyone else',
              multiplier: defaultPlanMultiplier,
            }}
            onChange={(planMultiplier) => set({ planMultiplier })}
          />
        ) : (
          <span className='organisation-field-hint'>
            Plan pricing is not available yet; this group follows the
            organisation plan.
          </span>
        )}
        <span className='organisation-field-hint'>
          A member in several groups gets the biggest plan of any of them.
        </span>
      </div>

      <details className='organisation-details'>
        <summary>Credit limits for this group (optional)</summary>
        <div className='organisation-field-grid'>
          <label className='organisation-field'>
            <span className='field-label'>Credits per member each month</span>
            <input
              type='number'
              className='field-input'
              min={0}
              placeholder='Same as everyone else'
              value={values.allocation}
              disabled={busy}
              onChange={(e) => set({ allocation: e.target.value })}
            />
            <span className='organisation-field-hint'>
              Only applies when credit sharing gives each member their own
              credits (Advanced).
            </span>
          </label>
          <label className='organisation-field'>
            <span className='field-label'>
              Shared-balance limit per member each month
            </span>
            <input
              type='number'
              className='field-input'
              min={0}
              placeholder='Same as everyone else'
              value={values.overflowCap}
              disabled={busy}
              onChange={(e) => set({ overflowCap: e.target.value })}
            />
            <span className='organisation-field-hint'>
              Only applies when members may use the shared balance.
            </span>
          </label>
        </div>
      </details>
      <div className='organisation-actions'>
        <button type='submit' className='btn-primary' disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        <button
          type='button'
          className='btn-secondary'
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const OrganisationGroups: FC<OrganisationGroupsProps> = ({
  groups,
  overrides,
  members,
  defaultPlanMultiplier,
  seatPlan,
  busyKey,
  onCreate,
  onUpdate,
  onDelete,
  onToggleMember,
}) => {
  const [creating, setCreating] = useState(false);
  const [createValues, setCreateValues] = useState<GroupFormValues>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<GroupFormValues>(EMPTY_FORM);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const overrideFor = (groupId: string) =>
    overrides.find((o) => o.groupId === groupId);
  const assignable = members.filter((m) => m.status !== 'declined');
  const memberName = new Map(members.map((m) => [m.id, m.name]));

  const submitCreate = async () => {
    const result = validate(createValues);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    setError(null);
    if (await onCreate(result.input, result.override)) {
      setCreating(false);
      setCreateValues(EMPTY_FORM);
    }
  };

  const submitEdit = async (group: OrganisationGroup) => {
    const result = validate(editValues);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    setError(null);
    if (await onUpdate(group, result.input, result.override)) {
      setEditingId(null);
    }
  };

  return (
    <div className='organisation-card' data-testid='organisation-groups'>
      <h3 className='organisation-card-title'>Groups</h3>
      <p className='organisation-card-text'>
        Group members however suits you — “Partners”, “Product team”, “Parents”
        — and give a group its own monthly plan or spending limits. Members can
        belong to any number of groups and see only the names of their own.
        Deleting a group never removes anyone from the organisation.
      </p>

      {groups.length === 0 && !creating && (
        <p className='organisation-empty'>
          No groups yet. Everyone is on the organisation plan
          {seatPlan
            ? ` (${planSentence(defaultPlanMultiplier, seatPlan)})`
            : ''}
          .
        </p>
      )}

      <ul className='organisation-group-list'>
        {groups.map((group) => {
          const override = overrideFor(group.id);
          const busy = busyKey === group.id;
          const editing = editingId === group.id;
          const assigning = assigningId === group.id;
          const meta: string[] = [
            group.planMultiplier === null
              ? 'Organisation plan'
              : planSentence(group.planMultiplier, seatPlan),
            `${group.membershipIds.length} ${group.membershipIds.length === 1 ? 'member' : 'members'}`,
          ];
          if (override?.allocation != null) {
            meta.push(
              `${formatCredits(override.allocation)} credits each a month`,
            );
          }
          if (override?.overflowCap != null) {
            meta.push(
              `shared-balance limit ${formatCredits(override.overflowCap)}`,
            );
          }

          return (
            <li
              key={group.id}
              className='organisation-group-item'
              data-testid='organisation-group'
            >
              <div className='organisation-group-row'>
                <div className='organisation-group-info'>
                  <span className='organisation-group-name'>{group.name}</span>
                  {group.description && (
                    <span className='organisation-group-description'>
                      {group.description}
                    </span>
                  )}
                  <span className='organisation-group-meta'>
                    {meta.join(' · ')}
                  </span>
                </div>
                {!editing && (
                  <div className='organisation-group-actions'>
                    <button
                      type='button'
                      className='btn-secondary'
                      disabled={busy}
                      onClick={() => {
                        setAssigningId(assigning ? null : group.id);
                        setConfirmDeleteId(null);
                      }}
                    >
                      {assigning ? 'Done' : 'Members'}
                    </button>
                    <button
                      type='button'
                      className='btn-secondary'
                      disabled={busy}
                      onClick={() => {
                        setEditValues(formFor(group, override));
                        setEditingId(group.id);
                        setAssigningId(null);
                        setConfirmDeleteId(null);
                        setError(null);
                      }}
                    >
                      Edit
                    </button>
                    {confirmDeleteId === group.id ? (
                      <>
                        <button
                          type='button'
                          className='btn-primary danger'
                          disabled={busy}
                          onClick={async () => {
                            if (await onDelete(group)) setConfirmDeleteId(null);
                          }}
                        >
                          {busy ? 'Deleting…' : `Delete “${group.name}”`}
                        </button>
                        <button
                          type='button'
                          className='btn-secondary'
                          disabled={busy}
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          Keep
                        </button>
                      </>
                    ) : (
                      <button
                        type='button'
                        className='btn-secondary danger'
                        disabled={busy}
                        onClick={() => setConfirmDeleteId(group.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>

              {editing && (
                <GroupForm
                  values={editValues}
                  busy={busy}
                  submitLabel='Save group'
                  seatPlan={seatPlan}
                  defaultPlanMultiplier={defaultPlanMultiplier}
                  onChange={setEditValues}
                  onSubmit={() => submitEdit(group)}
                  onCancel={() => {
                    setEditingId(null);
                    setError(null);
                  }}
                />
              )}

              {assigning && (
                <ul
                  className='organisation-group-members'
                  aria-label={`Members of ${group.name}`}
                >
                  {assignable.length === 0 && (
                    <li className='organisation-empty'>
                      Invite members first.
                    </li>
                  )}
                  {assignable.map((member) => {
                    const checked = group.membershipIds.includes(member.id);
                    const toggling = busyKey === `${group.id}:${member.id}`;
                    return (
                      <li key={member.id}>
                        <label className='organisation-checkbox'>
                          <input
                            type='checkbox'
                            checked={checked}
                            disabled={toggling || busy}
                            onChange={(e) =>
                              onToggleMember(group, member, e.target.checked)
                            }
                          />
                          <span>
                            {memberName.get(member.id) || 'Unnamed member'}
                            {member.seatKind === 'directory' && (
                              <span className='organisation-stat-sub'>
                                {' '}
                                · {SEAT_LABELS.directory}
                              </span>
                            )}
                            {member.status !== 'active' && (
                              <span className='organisation-stat-sub'>
                                {' '}
                                · {STATUS_LABELS[member.status]}
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {creating ? (
        <GroupForm
          values={createValues}
          busy={busyKey === 'group:new'}
          submitLabel='Create group'
          seatPlan={seatPlan}
          defaultPlanMultiplier={defaultPlanMultiplier}
          onChange={setCreateValues}
          onSubmit={submitCreate}
          onCancel={() => {
            setCreating(false);
            setError(null);
          }}
        />
      ) : (
        <div className='organisation-actions'>
          <button
            type='button'
            className='btn-secondary'
            onClick={() => {
              setCreating(true);
              setEditingId(null);
              setError(null);
            }}
          >
            New group
          </button>
        </div>
      )}

      {error && (
        <p className='organisation-feedback error' role='alert'>
          {error}
        </p>
      )}
    </div>
  );
};

export default OrganisationGroups;
